import { MyBusinessSchema } from '../../data/types';
import { formatBusinessLocation } from '../../utils/businessProfile';
import { capitalizeString } from '../../utils/helpers';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FlagIcon from '@mui/icons-material/Flag';
import { ReactComponent as LocationIcon } from '../../icons/ResourceLocationIcon.svg';
import { ReactComponent as MyBusinessIcon } from '../../icons/MyBusinessIcon.svg';

interface BusinessCardProps {
  business: MyBusinessSchema;
  onEdit?: (business: MyBusinessSchema) => void;
  onDelete?: (business: MyBusinessSchema) => void;
  onOpen?: (business: MyBusinessSchema) => void;
  onContactResearch?: (business: MyBusinessSchema) => void;
}

/** Card summarizing a saved My Business profile with optional logo. */
const BusinessCard = ({ business, onEdit, onDelete, onOpen, onContactResearch }: BusinessCardProps) => {
  return (
    <div
      className="bg-white rounded-2xl shadow-sm hover:shadow-lg p-5 transition-all duration-300 relative group border border-gray-100 cursor-pointer"
      onClick={() => onOpen?.(business)}
      role={onOpen ? 'button' : undefined}
    >
      <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
        {onEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(business);
            }}
            className="border-0 p-1.5 rounded-lg bg-white/90 backdrop-blur-sm hover:bg-primaryDark-400/10 transition-all duration-200"
            aria-label="Edit business"
          >
            <EditIcon className="w-2 h-2 text-primaryDark-400 hover:text-primaryDark-100 transition-colors" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(business);
            }}
            className="border-0 p-1.5 rounded-lg bg-white/90 backdrop-blur-sm hover:bg-red-50 transition-all duration-200"
            aria-label="Delete business"
          >
            <DeleteIcon className="w-4 h-4 text-red-400 hover:text-red-600 transition-colors" />
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-3 pr-12">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-[#EFF8FA] flex items-center justify-center border border-gray-100 shrink-0">
            {business.logo ? (
              <img src={business.logo} alt="" className="w-full h-full object-contain bg-white" />
            ) : (
              <MyBusinessIcon className="w-6 h-6 text-primaryDark-300" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-xl font-raleway font-semibold text-primaryDark-100 mb-1 truncate">
              {business.business_name}
            </h3>
            <div className="flex items-center">
              <div className="w-1 h-4 bg-primaryDark-300 rounded-full mr-2"></div>
              <p className="text-primaryDark-400 text-sm truncate">
                {business.business_category}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3.5 mt-2">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primaryDark-400/10">
              <MyBusinessIcon className="w-4 h-4 text-primaryDark-300" />
            </div>
            <span className="text-sm text-gray-400">{business.business_type}</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primaryDark-400/10">
              <LocationIcon className="w-4 h-4 text-primaryDark-300" />
            </div>
            <span className="text-sm text-gray-400">{formatBusinessLocation(business)}</span>
          </div>

          <div className="flex flex-row items-center rounded-lg px-1.5 py-1 bg-primaryDark-400/30 w-fit">
            <FlagIcon style={{ fontSize: 16 }} className="text-primaryDark-200" />
            <div className="flex items-center mx-1">
              <span className="line-clamp-1 text-center text-wrap text-[12px] font-raleway font-semibold text-primaryDark-200">
                {capitalizeString(business.business_stage)} Stage
              </span>
            </div>
          </div>

          {onContactResearch && (
            <button
              type="button"
              className="border-0 bg-transparent p-0 text-sm font-raleway font-semibold text-primaryDark-300"
              onClick={(event) => {
                event.stopPropagation();
                onContactResearch(business);
              }}
            >
              Contact research
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessCard;
